<template>
   <div
      v-if="document?.content && Array.isArray(document.content)"
      :class="usesContainerQueries ? 'rich-text-content-container' : 'rich-text-content-media'"
      data-test="richtext-container"
   >
      <div v-for="(node, nodeIndex) in document.content" :key="nodeIndex">
         <component :is="processNode(node, nodeIndex)" />
      </div>
   </div>
</template>

<script setup lang="ts">
import schema from "./rich-text-schema.js";
import BlogpostImage from "./BlogpostImage.vue";
import ImageCredit from "./ImageCredit.vue";

const COMPONENTS_FOR_CUSTOM_NODES = {
   image: BlogpostImage,
   imageCreditList: ImageCredit,
};

const props = withDefaults(
   defineProps<{
      document: any;
      context?: any; // props or any data passed to a custom node
      usesContainerQueries?: boolean;
   }>(),
   {
      context: {},
      usesContainerQueries: false,
   }
);

onMounted(() => {
   if (!props.document?.content || !Array.isArray(props.document?.content)) {
      console.error("[rich-text-renderer]: Wrong data type passed to the 'document' prop.");
   }
});

function processNode(node, nodeIdentifier) {
   // Recursive function that renders ProseMirror's nodes as VNodes.
   if (node.marks && node.marks.length) {
      // For each node, if it is one with 'marks' (e.g. <i>) return the rendered marks and text
      return renderMarks(node.marks, node.text);
   } else if (node.text) {
      // If there are no 'marks', but there is text directly within the node, render just the text
      return node.text;
   } else {
      // Otherwise, we need to render a particular node type
      const { tag, data = {}, skipContent } = getMatchingNode(node, `${nodeIdentifier}`);

      const allData = { ...data, ...props.context };

      // If that node also has content inside, we need to call this function again for the child nodes
      if (node.content && !skipContent) {
         return h(
            tag,
            allData,
            node.content.map((childNode, childNodeIndex) =>
               processNode(childNode, `${nodeIdentifier}-${childNodeIndex}`)
            )
         );
      } else {
         return h(tag, allData, () => node.content);
      }
   }
}

function renderMarks(marks, text) {
   // Returns nested marks and the inner text. Ex: <strike><b><i>Hello World</i></b></strike>
   // Get the first 'mark'. We'll always get a 'tag' back, and possbily 'data' or 'skipContent'
   const { tag, data, skipContent, isVueComponent } = getMatchingMark(marks[0]);

   if (marks.length === 1 || skipContent) {
      // If this is the only mark, we can render

      const dataWithProps = { ...data, text }; // pass text as prop
      const content = isVueComponent ? () => text : text; // the content for vue components must be a function, because that's the type of value expected by slots

      return h(tag, dataWithProps, content);
   }

   // Otherwise we want to call this function again
   return h(tag, data, [renderMarks(marks.slice(1), text)]);
}

function getMatchingMark(item) {
   if (typeof schema.marks[item.type] !== "function") {
      console.info(`No schema found for {${item.type}} mark!`);
      // Return empty element
      return { tag: "span" };
   }
   return schema.marks[item.type](item);
}

function getMatchingNode(item, nodeIdentifier) {
   if (Object.keys(COMPONENTS_FOR_CUSTOM_NODES).includes(item.type)) {
      // Custom nodes are registered here, rather than in schema, as we need to use resolveComponent
      // https://v3.vuejs.org/guide/migration/render-function-api.html#_2-x-syntax-4
      // Pass through any props that are needed in 'data'
      const data = { ...item.attrs };
      let skipContent = false;
      if (item.type === "imageCreditList") {
         data.node = item;
         skipContent = true;
      }

      return { tag: COMPONENTS_FOR_CUSTOM_NODES[item.type], data, skipContent };
   } else if (typeof schema.nodes[item.type] !== "function") {
      console.error(`No schema found for '${item.type}' node!`);
      // Return empty element
      return { tag: "div", skipContent: true };
   } else {
      // Return node info from schema
      const nodeInfo = schema.nodes[item.type](item);

      return nodeInfo;
   }
}
</script>
